


// export interface IValueConvertionService extends IValueConverter {
// }

import { CancellationToken } from "../../../base/common/cancellation";

/** `${constructorName}->${targetType}` pairs already reported — see convert(). */
const _reportedConversionMisses = new Set<string>();

export const ValueConversionService = new class implements IValueConverter {

    private readonly converters: IValueConverter[] = [];

    registerConverter(convert: IValueConverter): void {
        if (this.converters.includes(convert)) {
            return;
        }
        this.converters.push(convert);
    }

    canConvert(value: unknown, usage: ValueConverterOptions): boolean {
        for (const converter of this.converters) {
            try {
                if (converter.canConvert(value, usage)) {
                    return true;
                }
            }
            catch (e) {
                console.error("Error during value conversion check with converter:", converter, e);
            }
        }
        return false;
    }

    async convert<T extends unknown>(value: unknown, usage: ValueConverterOptions): Promise<T | null> {
        for (const converter of this.converters) {
            try {
                if (converter.canConvert(value, usage)) {
                    return converter.convert<T>(value, usage);
                }
            }
            catch (e) {
                console.error("Error during value conversion with converter:", converter, e);
            }
        }

        // No converter found (only log if value is not null/undefined).
        //
        // ONCE per kind, not per call. This runs inside the Inspector's reactive
        // property cascade, so a single selection can miss hundreds of times; each
        // console.warn captures and symbolicates a deep stack, and with DevTools
        // open that alone froze the click handler for ~1s on a heavy page
        // (next.junni.co.jp, 2026-07-28 — the pick itself was only 13ms of it).
        // The constructor name is included so a genuinely-unsupported type is
        // still identifiable from one line.
        if (value !== null && value !== undefined) {
            const kind = (value as { constructor?: { name?: string } })?.constructor?.name ?? typeof value;
            const key = `${kind}->${usage.targetType}`;
            if (!_reportedConversionMisses.has(key)) {
                _reportedConversionMisses.add(key);
                console.warn(`ValueConversionService: No converter found for ${kind} → ${usage.targetType} (further occurrences suppressed)`);
            }
        }


        return null;
    }
}




// #region Property Value Converter

export interface ValueConverterOptions {
    token?: CancellationToken,
    targetType: "texture" | "blob:url" // e.g., "image/png", "image/jpeg", etc.
    width?: number,
    height?: number,
    usageHint?: "skybox",
    /** Original asset name when `value` is a blob URL without an extension. */
    sourceName?: string,
    /** Environment encoding that cannot be inferred from the loadable URL. */
    environmentFormat?: "pmrem",
}

export interface IValueConverter {
    canConvert(value: unknown, target: ValueConverterOptions): boolean;
    convert<T extends unknown>(value: unknown, target: ValueConverterOptions): Promise<T | null>;
}
